<script lang="ts">
interface Props {
    existingColli?: FormShipmentColli
}
</script>
<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { v4 as uuid } from 'uuid'
import { computed, watch } from 'vue'

import { FormShipmentColli } from '@/types/delivery-management'
import useForm from '@/hooks/use-form'
import { useAuthStore } from '@/stores/auth-store'
import { IncludedCompany } from '@/types/company'

import MyInput from '@/components/my-components/form/MyInput.vue'

interface Form {
    id: string
    name: string
    colliNumber: string
    volume: number
    height: number
    width: number
    length: number
    weight: number
    amount: number
}

const authStore = useAuthStore()

const emit = defineEmits<{
    (e: 'update', value: FormShipmentColli): void
}>()

const props = defineProps<Props>()

const { t } = useI18n()

const { data } = useForm<Form>({
    id: props.existingColli?.colli.id ?? uuid(),
    name: props.existingColli?.colli.name ?? '',
    colliNumber: props.existingColli?.colli.colliNumber ?? '',
    volume: props.existingColli?.colli.volume ?? 0,
    height: props.existingColli?.colli.height ?? 0,
    width: props.existingColli?.colli.width ?? 0,
    length: props.existingColli?.colli.length ?? 0,
    weight: props.existingColli?.colli.weight ?? 0,
    amount: props.existingColli?.amount ?? 0,
})

const calculatedVolume = computed(() => {
    return Math.round((data.height / 100) * (data.width / 100) * (data.length / 100))
})

function convertToFormShipmentColli(data: Form): FormShipmentColli {
    return {
        colli: {
            id: data.id,
            name: data.name,
            colliNumber: data.colliNumber,
            volume: Number(calculatedVolume.value),
            height: data.height,
            width: data.width,
            length: data.length,
            weight: data.weight,
            company: {
                id: authStore.companyId,
            } as IncludedCompany,
            // Nullable fields
            description: null,
            tickets: [],
            shipment: null,
            tour: null,
            pickupDate: null,
            deliveryDate: null,
            minimumTemperature: null,
            maximumTemperature: null,
            unNumber: null,
            adrInformation: null,
            createdAt: '',
            updatedAt: '',
            deletedAt: null,
        },
        amount: data.amount,
    }
}

watch(
    () => ({ ...data }),
    (newData) => {
        emit('update', convertToFormShipmentColli(newData))
    },
    { deep: true, immediate: true },
)
</script>

<template>
    <div class="flex w-full space-x-3 py-2 px-3 rounded-xl shadow-sm bg-white dark:bg-dark-600">
        <div class="flex text-xs w-1/3 space-x-4 items-center">
            <MyInput
                v-model="data.name"
                name="name"
                class="h-9"
                type="text"
                placeholder="name"
                :label="t('name')"
                required
            />
            <MyInput
                v-model="data.colliNumber"
                name="colliNumber"
                class="h-9"
                type="text"
                placeholder="colli Number"
                :label="t('colliNumber')"
                required
            />
        </div>

        <div class="flex text-xs w-2/4 space-x-4 items-center">
            <MyInput
                v-model="data.length"
                name="length"
                class="h-9"
                type="number"
                placeholder="length"
                :label="t('length')"
            >
                <template #button>
                    <span class="absolute top-2 bottom-1 right-2 m-auto h-5">cm</span>
                </template>
            </MyInput>

            <MyInput
                v-model="data.width"
                name="width"
                class="h-9"
                type="number"
                placeholder="width"
                :label="t('width')"
            >
                <template #button>
                    <span class="absolute top-2 bottom-1 right-2 m-auto h-5">cm</span>
                </template>
            </MyInput>

            <MyInput
                v-model="data.height"
                name="height"
                type="number"
                class="w-full h-9"
                placeholder="height"
                :label="t('height')"
            >
                <template #button>
                    <span class="absolute top-2 bottom-1 right-2 m-auto h-5">cm</span>
                </template>
            </MyInput>

            <MyInput
                :value="calculatedVolume"
                name="volume"
                class="h-9"
                type="number"
                placeholder="volume"
                :label="t('volume')"
                disabled
            >
                <template #button>
                    <span class="absolute top-2 bottom-1 right-2 m-auto h-5">cbm</span>
                </template>
            </MyInput>
        </div>

        <div class="flex text-xs w-1/4 items-center space-x-4">
            <MyInput
                v-model="data.weight"
                name="weight"
                class="h-9"
                type="number"
                placeholder="weight"
                :label="t('weight')"
            >
                <template #button>
                    <span class="absolute top-2 bottom-1 right-2 m-auto h-5">kg</span>
                </template>
            </MyInput>
            <MyInput
                v-model="data.amount"
                name="amount"
                class="h-9"
                :label="t('amount')"
                type="number"
                min="1"
                required
            />
            <slot name="suffix" />
        </div>
    </div>
</template>
